import { HTMLAttributes, forwardRef } from 'react';

import { Theme, WithStyles, createStyles, withStyles } from '@material-ui/core';

import { RiReplyLine } from '@remixicon/react';
import classNames from 'classnames';
import { formatDistanceToNow } from 'date-fns';

import { Flex, Text } from '@backstage/ui';

import {
  IdentityRefFragment,
  ReactionSummaryFragment,
} from '@spotify/confidence-graphql';
import {
  EmojiReactions,
  TypographyWithMentions,
  useReactions,
} from '@spotify/plugin-confidence-comments-react';

import Avatar from './Avatar';
import ReactionButton from './ReactionButton';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1.5),
    },
    container: {},
    content: {
      flex: 1,
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    author: {
      paddingTop: theme.spacing(0.75),
      fontWeight: 'bold',
    },
    actions: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    },
  });

export interface CommentProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'classes'>,
    WithStyles<typeof styles> {
  /** The comment's resource name for reactions */
  name: string;
  author: IdentityRefFragment | null;
  createTime: string;
  content: string;
  /** Emoji reactions on this comment */
  reactions?: ReactionSummaryFragment | null | undefined;
  onReply?: () => void;
}

const Comment = forwardRef<HTMLDivElement, CommentProps>((props, ref) => {
  const {
    className,
    classes,
    name,
    author,
    createTime,
    content,
    reactions,
    onReply,
    ...rest
  } = props;

  const { reactedEmojis, toggleReaction, emojiReactions } = useReactions(
    name,
    reactions,
  );

  return (
    <div ref={ref} className={classNames(classes.root, className)} {...rest}>
      <Avatar identity={author} size="small" />
      <div className={classes.container}>
        <Flex align="baseline" gap="1">
          <Text variant="body-medium" className={classes.author}>
            {author?.displayName ?? author?.name}
          </Text>
          <Text variant="body-small" color="secondary">
            {formatDistanceToNow(new Date(createTime), {
              addSuffix: true,
            })}
          </Text>
        </Flex>
        <TypographyWithMentions
          text={content}
          variant="body1"
          className={classes.content}
        />
        <div className={classes.actions}>
          {onReply && (
            <ReactionButton
              size="small"
              iconComponent={RiReplyLine}
              onClick={onReply}
              variant="tertiary"
            />
          )}
          <EmojiReactions
            compact
            emojiReactions={emojiReactions}
            userReactedEmojis={reactedEmojis}
            onToggleReaction={toggleReaction}
          />
        </div>
      </div>
    </div>
  );
});

Comment.displayName = 'ActivityFeedItem.Comment';

export default withStyles(styles)(Comment);
